import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { DocCommentChildAction, DocCommentChildActionTypes, DocumentationContextType, DocPage, DocPageComment, DocumentationProps, BasicProviderProps, CommentOriginType } from '../types';
import ErrorBoundary from '../components/error/ErrorBoundary';
import { AppContext } from './AppContext';
import useDataClient from '../hooks/useDataClient';
import { documentationAdapter } from '../adapter';
import { extractConfluenceId, showItemInView } from '../helpers/editor.helper';

export const DocumentationContext = createContext<DocumentationContextType | null>(null);

export default function DocumentationProvider({ children }: BasicProviderProps) {
    const appContext = useContext(AppContext)
    const documentationClient = useDataClient<DocPage>({ appContext: appContext })
    const commentsClient = useDataClient<DocPageComment[]>({ appContext: appContext })
    const commentsEditorClient = useDataClient<DocPageComment>({ appContext: appContext })
    const commentsNewResourceClient = useDataClient<DocPageComment>({ appContext: appContext })
    const commentsDeleteClient = useDataClient<DocPageComment>({ appContext: appContext })
    const [showCommentEditor, setShowCommentEditor] = useState(false)
    const [idDocuments, setIdDocuments] = useState<number[]>([])
    const [commentTarget, setCommentTarget] = useState<DocCommentChildAction | null>(null)
    const [idCommentUpdated, setIdCommentUpdated] = useState('')
    const [reloadExternalContent, setReloadExternalContent] = useState(false)
    const [referencedDoc, setReferencedDoc] = useState<DocumentationProps | null>(null)

    const page = useMemo<DocPage | null>(() => {
        let pageData = documentationClient.status.data || null
        if (pageData && commentsClient.status.data) {
            pageData.comments = commentsClient.status.data
        }
        pageData?.comments.sort((a, b) => a.id - b.id)
        return pageData
    }, [documentationClient.status.data, commentsClient.status.data])

    const handleSetActionTarget = useCallback((data: DocPageComment | null, action?: DocCommentChildActionTypes) => {
        if (data && action) {
            setCommentTarget({ data, action })
            setIdCommentUpdated(String(data.id))
        } else {
            setCommentTarget(null)
        }
    }, [])

    const handleNewComment = useCallback((value: boolean) => {
        setShowCommentEditor(value)
        setCommentTarget(null)
    }, [])

    const handleCommentCancel = useCallback(() => {
        setShowCommentEditor(false)
        setCommentTarget(null)
        showItemInView(idCommentUpdated)
    }, [])

    const handlePageHiperLink = useCallback((destination: string) => {
        let tryConfluenceId = extractConfluenceId(destination)
        if (destination.includes('#')) {
            showItemInView(destination.split('#')[1])
        } else if (tryConfluenceId) {
            setIdDocuments(prev => [...prev, tryConfluenceId])
        } else {
            window.open(destination, "_blank")
        }
    }, [])

    const handleCommentSubmit = useCallback((commentContent: string) => {
        let commentData: DocPageComment = {
            id: commentTarget?.data.id || 0,
            content: commentContent,
            comments: [],
            version: commentTarget?.data.version || 0,
            pageTitle: referencedDoc?.title,
            pageId: idDocuments[idDocuments.length - 1]
        }
        let resourceTarget = referencedDoc?.forum?.target
        let origin: CommentOriginType = resourceTarget ? 'resource' : 'blog'
        commentsEditorClient.clean()
        switch (true) {
            case commentTarget?.action === 'writing':
                commentData.parentCommentId = commentTarget?.data.id
                commentData.parentCommentUser = commentTarget?.data.user
                commentsEditorClient.executeRequest(documentationAdapter.addComment(commentData, origin))
                break;
            case commentTarget?.action === 'editing':
                commentsEditorClient.executeRequest(documentationAdapter.editComment(commentData))
                break;
            default:
                idDocuments.length === 0 && resourceTarget ? commentsNewResourceClient.executeRequest(documentationAdapter.createCommentResource(resourceTarget, commentData)) :
                    commentsEditorClient.executeRequest(documentationAdapter.addComment(commentData, origin))
                break;
        }
    }, [idDocuments, commentTarget, referencedDoc])

    const handleCommentDelete = useCallback((comment: DocPageComment) => {
        if (window.confirm(`¿Estas seguro de eliminar este comentario?`) == true) {
            commentsDeleteClient.executeRequest(documentationAdapter.deleteComment(comment))
        }
    }, [])

    const handleBackHistory = useCallback(() => {
        let idHistory = [...idDocuments]
        idHistory.pop()
        setIdDocuments(idHistory)
    }, [idDocuments])


    const contextData = useMemo<DocumentationContextType>(() => ({
        page,
        loadingPage: documentationClient.status.loading,
        loadingComments: commentsClient.status.loading || commentsDeleteClient.status.loading,
        currentUser: appContext?.user.data?.user || 'Api-docs',
        handlePageHiperLink,
        handleBackHistory: idDocuments.length > 1 ? handleBackHistory : null,
        handleCommentSubmit,
        handleCommentDelete,
        showCommentEditor,
        setShowCommentEditor: handleNewComment,
        commentTarget,
        setCommentTarget: handleSetActionTarget,
        handleCommentCancel,
        referencedDoc,
        setReferencedDoc,
        reloadExternalContent
    }), [page, referencedDoc, documentationClient.status, idDocuments, showCommentEditor, commentTarget, reloadExternalContent])

    useEffect(() => {
        if (referencedDoc?.reference) {
            setIdDocuments(referencedDoc.cleanHistory ? [extractConfluenceId(referencedDoc.reference)] : [...idDocuments, extractConfluenceId(referencedDoc.reference)])
        } else {
            setIdDocuments([])
            commentsClient.clean()
            documentationClient.clean()
            setReloadExternalContent(false)
        }
    }, [referencedDoc?.reference])

    useEffect(() => {
        let lastElement = idDocuments.length ? idDocuments[idDocuments.length - 1] : null
        handleSetActionTarget(null)
        commentsClient.clean()
        commentsEditorClient.clean()
        if (lastElement) {
            documentationClient.executeRequest(
                documentationAdapter.getDocumentation(lastElement, referencedDoc?.withComments || referencedDoc?.forum?.active)
            )
        }
    }, [idDocuments])

    useEffect(() => {
        if (commentsEditorClient.status.data) {
            handleCommentCancel()
            setIdCommentUpdated(String(commentsEditorClient.status.data.id))
            commentsClient.executeRequest(documentationAdapter.getComments(idDocuments[idDocuments.length - 1]))
        }
    }, [commentsEditorClient.status.data])

    useEffect(() => {
        if (commentsNewResourceClient.status.data) {
            handleCommentCancel()
            setReloadExternalContent(true)
        }
    }, [commentsNewResourceClient.status.data])

    useEffect(() => {
        if (commentsDeleteClient.status.data) {
            setIdCommentUpdated('')
            commentsClient.executeRequest(documentationAdapter.getComments(idDocuments[idDocuments.length - 1]))
        }
    }, [commentsDeleteClient.status.data])

    useEffect(() => {
        if (commentsClient.status.data && idCommentUpdated) {
            showItemInView(idCommentUpdated)
        }
    }, [commentsClient.status.data])

    return (
        <ErrorBoundary>
            <DocumentationContext.Provider value={contextData}>
                {children}
            </DocumentationContext.Provider>
        </ErrorBoundary>
    )
}